/**
 * BreadcrumbList JSON-LD — one implementation, two consumers.
 *
 *   - prerender.mjs   bakes it into the static HTML so crawlers see it
 *   - useSEO.ts       re-applies it on client-side navigation
 *
 * Adding a page needs no schema code: add a `breadcrumbs` array to that route's
 * entry in seoData.ts and both paths pick it up on the next build.
 *
 * The route metadata describes each page's position in the site hierarchy and
 * is used only for structured data; no visual breadcrumb navigation is rendered.
 */
const DEFAULT_SITE_URL = 'https://thenamewheel.com';

export interface Crumb {
  /** Concise label for this level of the site hierarchy. */
  name: string;
  /** Site-root-relative path, e.g. '/gallery'. Use '/' for Home. */
  path: string;
}

export interface BreadcrumbListLd {
  '@context': 'https://schema.org';
  '@type': 'BreadcrumbList';
  itemListElement: {
    '@type': 'ListItem';
    position: number;
    name: string;
    item: string;
  }[];
}

/**
 * Absolute URL for a crumb, in the site's preferred trailing-slash form.
 * '/' maps to the bare origin (https://thenamewheel.com — no double-slash).
 * Every other path gets a trailing slash so the BreadcrumbList `item` URLs
 * match the canonical URLs on those pages exactly.
 */
function crumbUrl(path: string, siteUrl: string): string {
  if (path === '/') return `${siteUrl.replace(/\/$/, '')}/`;
  const norm = path.endsWith('/') ? path : path + '/';
  return `${siteUrl}${norm}`;
}

/**
 * Build a BreadcrumbList document from an ordered trail.
 * Returns null for an empty or single-item trail — a one-item breadcrumb is not
 * a trail, and emitting it adds noise without earning a rich result.
 */
export function buildBreadcrumbList(
  crumbs: Crumb[] | undefined,
  siteUrl: string = DEFAULT_SITE_URL,
): BreadcrumbListLd | null {
  if (!crumbs || crumbs.length < 2) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: crumbUrl(c.path, siteUrl),
    })),
  };
}

/** Stable element id, so the prerendered block is replaced rather than duplicated. */
export const BREADCRUMB_LD_ID = 'breadcrumb-jsonld';
